import { useAtomValue, useSetAtom } from 'jotai';
import { forwardRef, useCallback, useMemo } from 'react';

import { Card } from '@growtherapy/sprout-ui';

import {
  MeasureInsightTableConnected,
  type MeasureInsightTableConnectedProps,
  useRegisterDrawerRef,
} from '@grow-therapy-team/seedling-components';

import { useCaptureComponentView } from '../../hooks/useCaptureComponentView';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum, UserViewedParams } from '../../segment/types';
import { mergeRefs } from '../../utils';
import { findMeasureBundleInfoByResponseShortId } from '../measures/utils';
import {
  ProviderDrawerState,
  drawerStateAtom,
  patientHasCompletedMeasureResponsesFamily,
  providerShortIdAtom,
  selectedBundleAtom,
  selectedClientInfoAtom,
  selectedClientMeasuresData as selectedClientMeasuresDataAtom,
  selectedClientUserShortIdAtom,
  selectedMeasureBundleItemIdAtom,
} from '../state';
import { PROGRESS_TITLE } from './constants';
import { DrawerRefKey } from './utils/types';

type WrapperProps = Omit<Partial<MeasureInsightTableConnectedProps>, 'clientUserShortId' | 'providerShortId'>;

export const ClientInformationProgress = forwardRef<HTMLDivElement, MeasureInsightTableConnectedProps>(
  (measureInsightTableProps, ref) => {
    return (
      <div
        className='flex flex-col gap-2'
        ref={ref}
        // tabIndex={-1} allows the div to be programmatically focused when
        // navigating from pre-visit summary resource buttons, but excludes it
        // from keyboard tab order
        tabIndex={-1}
        aria-labelledby='measures-progress-title'
      >
        <Card
          title={PROGRESS_TITLE}
          titleAs='h3'
          id='measures-progress-title'
          className='rebrand:bg-surface-inverseEmphasis'
          titleClassName='rebrand:text-text-inverse'
        >
          <MeasureInsightTableConnected isInverse {...measureInsightTableProps} />
        </Card>
      </div>
    );
  },
);
ClientInformationProgress.displayName = 'ClientInformationProgress';

// statically defined because:
// 1) we only show the table view of measure progress
// 2) this component only renders in-session
const LOGGING_PROPERTIES: UserViewedParams<EventTypeEnum.MEASURE_PROGRESS_VIEWED> = {
  measureProgressView: 'table',
  inSession: true,
};

export function ClientInformationProgressWrapper({
  isProvider = true,
  isShownWithinPaperworkFlow = false,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  ...passthroughProps
}: WrapperProps) {
  const measuresRef = useRegisterDrawerRef<DrawerRefKey, HTMLDivElement>(DrawerRefKey.MEASURES_CARD);
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setSelectedBundle = useSetAtom(selectedBundleAtom);
  const setSelectedMeasureBundleItemId = useSetAtom(selectedMeasureBundleItemIdAtom);

  const selectedClientInfo = useAtomValue(selectedClientInfoAtom);
  const patientShortId = selectedClientInfo?.patientShortId;

  const measureBundles = useAtomValue(selectedClientMeasuresDataAtom);
  const hasCompletedMeasureResponses = useAtomValue(patientHasCompletedMeasureResponsesFamily(patientShortId ?? ''));
  // Segment events
  const { userClicked } = useTrackEvent();
  const { ref: viewTrackingRef } = useCaptureComponentView({
    type: EventTypeEnum.MEASURE_PROGRESS_VIEWED,
    properties: LOGGING_PROPERTIES,
  });

  const mergedRefs = useMemo(() => mergeRefs(measuresRef, viewTrackingRef), [measuresRef, viewTrackingRef]);

  const handleMeasureResponseClick = useCallback(
    (measureResponseShortId: string) => {
      userClicked(EventTypeEnum.MEASURE_PROGRESS_CLICKED, LOGGING_PROPERTIES);
      if (!measureResponseShortId || !measureBundles) return;
      const { measureBundle: selectedMeasureBundle, measureBundleItem: selectedMeasureBundleItem } =
        findMeasureBundleInfoByResponseShortId(measureBundles, measureResponseShortId);
      if (!selectedMeasureBundle || !selectedMeasureBundleItem) return;

      setSelectedBundle(selectedMeasureBundle);
      setSelectedMeasureBundleItemId(selectedMeasureBundleItem.measureBundleItemId?.toString() ?? '');
      setDrawerState(ProviderDrawerState.MEASURE_RESPONSES);
    },
    [measureBundles, setDrawerState, setSelectedBundle, setSelectedMeasureBundleItemId, userClicked],
  );

  if (!clientUserShortId || !providerShortId || !hasCompletedMeasureResponses) {
    return null;
  }

  return (
    <ClientInformationProgress
      {...passthroughProps}
      shouldAbbreviateScoreCategoryLabels={true}
      clientUserShortId={clientUserShortId}
      providerShortId={providerShortId}
      isProvider={isProvider}
      isShownWithinPaperworkFlow={isShownWithinPaperworkFlow}
      timezone={timezone}
      onClick={measureBundles ? handleMeasureResponseClick : undefined}
      ref={mergedRefs}
    />
  );
}
